// src/components/Header.js
import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import Icon from './Icon';
import Logo from './Logo';
import { GAMES, gameIcon } from '../data';
import { useAuth, displayNameOf } from '../context/AuthContext';
import { WalletChip } from '../economy/WalletContext';

function useDropdown() {
  const [open, setOpen] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(null);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(null);
    document.addEventListener('pointerdown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, []);
  return { open, setOpen, ref };
}

export default function Header({ onOpenProfile, onEnablePush, pushAvailable }) {
  const { user, userData, login, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const { open, setOpen, ref } = useDropdown();
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    setOpen(null);
  }, [location.pathname, setOpen]);

  const toggle = (id) => setOpen((cur) => (cur === id ? null : id));
  const name = displayNameOf(user, userData);
  const initials = name.slice(0, 2).toLocaleUpperCase('tr-TR');

  return (
    <header className="site-header">
      <div className="container header-inner" ref={ref}>
        <Link to="/" className="brand" aria-label="Oyuntu ana sayfa">
          <Logo sub="Kart Oyunları" />
        </Link>

        <WalletChip />

        <button type="button" className="menu-toggle" aria-expanded={menuOpen} aria-controls="main-nav" aria-label={menuOpen ? 'Menüyü kapat' : 'Menüyü aç'} onClick={() => setMenuOpen((v) => !v)}>
          <Icon name={menuOpen ? 'close' : 'menu'} />
        </button>

        <nav id="main-nav" className={`main-nav${menuOpen ? ' is-open' : ''}`} aria-label="Ana menü">
          <div className={`dropdown-wrap${open === 'games' ? ' is-open' : ''}`}>
            <button type="button" className="nav-link" aria-expanded={open === 'games'} aria-haspopup="true" onClick={() => toggle('games')}>
              Oyunlar <Icon name="chevron" size="sm" className="chevron" />
            </button>
            <div className="dropdown-panel" role="menu">
              <div className="panel-grid">
                {GAMES.map((g) => (
                  <Link key={g.id} to={`/oyun/${g.id}`} className="cat-item" role="menuitem">
                    <img src={gameIcon(g.id)} alt="" width="36" height="36" loading="lazy" />
                    <span className="cat-text">
                      <span className="cat-title">{g.name}</span>
                      <span className="cat-tagline">{g.players}</span>
                    </span>
                  </Link>
                ))}
              </div>
              <div className="panel-foot">
                <Link to="/#oyunlar">Tüm oyunlar →</Link>
                <Link to="/rehber">Oyun rehberleri →</Link>
              </div>
            </div>
          </div>
          <NavLink to="/rehber" className="nav-link">Nasıl Oynanır?</NavLink>
          <NavLink to="/lider-tablosu" className="nav-link">Lider Tabloları</NavLink>
          <NavLink to="/oyuntu/hakkimizda" className="nav-link">Hakkımızda</NavLink>

          {user ? (
            <div className={`dropdown-wrap align-end${open === 'user' ? ' is-open' : ''}`}>
              <button type="button" className="nav-link user-btn" aria-expanded={open === 'user'} aria-haspopup="true" onClick={() => toggle('user')}>
                {user.photoURL ? <img className="avatar" src={user.photoURL} alt="" width="32" height="32" referrerPolicy="no-referrer" /> : <span className="avatar">{initials}</span>}
                <span>{name}</span>
                <Icon name="chevron" size="sm" className="chevron" />
              </button>
              <div className="dropdown-panel sm" role="menu">
                <button type="button" className="cat-item" role="menuitem" onClick={() => { setOpen(null); onOpenProfile(); }}>
                  <span className="cat-icon"><Icon name="user" size="sm" /></span>
                  <span className="cat-text"><span className="cat-title">Profilim</span><span className="cat-tagline">Puanlar, sıralama, takma ad</span></span>
                </button>
                {userData?.nickname && (
                  <Link className="cat-item" role="menuitem" to={`/profil/${encodeURIComponent(userData.nickname)}`}>
                    <span className="cat-icon" style={{ '--c': '#1fadf0' }}><Icon name="trophy" size="sm" /></span>
                    <span className="cat-text"><span className="cat-title">Herkese açık profil</span><span className="cat-tagline">@{userData.nickname}</span></span>
                  </Link>
                )}
                <Link className="cat-item" role="menuitem" to="/arkadaslar">
                  <span className="cat-icon" style={{ '--c': '#a0139f' }}><Icon name="users" size="sm" /></span>
                  <span className="cat-text"><span className="cat-title">Arkadaşlarım</span><span className="cat-tagline">Mesaj ve Kristal gönder</span></span>
                </Link>
                {pushAvailable && (
                  <button type="button" className="cat-item" role="menuitem" onClick={() => { setOpen(null); onEnablePush(); }}>
                    <span className="cat-icon" style={{ '--c': '#3fae2a' }}><Icon name="bell" size="sm" /></span>
                    <span className="cat-text"><span className="cat-title">Bildirimleri aç</span><span className="cat-tagline">Yeni oyunlardan haberdar ol</span></span>
                  </button>
                )}
                <button type="button" className="cat-item" role="menuitem" onClick={() => { setOpen(null); logout(); }}>
                  <span className="cat-icon" style={{ '--c': '#f3380e' }}><Icon name="logout" size="sm" /></span>
                  <span className="cat-text"><span className="cat-title">Çıkış yap</span></span>
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="oy-btn oy-btn-dark oy-btn-sm nav-cta" onClick={login}>
              Google ile Giriş
            </button>
          )}
        </nav>
      </div>
    </header>
  );
}
